import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import Quill from 'quill';
import { Range } from 'quill/core/selection';

import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { SmilesService } from '@sb-tools/typescript/services/smiles.service';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { InboxAttachmentBase } from '@sb-tools/typescript/models/inbox-attachment-base.model';
import { NoteGuiModel } from '@sb-tools/typescript/models/view/note-gui-model.model';

import { ReplyTemplateService } from '../../../../sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { ToolbarHelperService } from '../../../core/services/toolbar-helper.service';
import { NoteService } from '../../../core/services/note.service';
import { QuillReplyTemplatesComponent } from '../../../../sb-modules/Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { SbQuillMentionComponent } from '../sb-quill-mention/sb-quill-mention.component';
import { QuillEditorComponent } from 'ngx-quill';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { QuillEmojiPickerComponent } from '../../../../sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutsideDirective } from '@sb-tools/typescript/directives/click-outside.directive';

type ToolbarSize = 'medium' | 'small' | 'xsmall';

@Component({
    selector: 'app-note-quill-editor',
    templateUrl: './note-quill-editor.component.html',
    styleUrls: ['./note-quill-editor.component.scss'],
    imports: [
        QuillReplyTemplatesComponent,
        SbQuillMentionComponent,
        QuillEditorComponent,
        NgClass,
        FormsModule,
        TooltipModule,
        InlineSVGComponent,
        QuillEmojiPickerComponent,
        TranslateModule,
        ClickOutsideDirective,
    ],
})
export class NoteQuillEditorComponent implements OnInit, OnDestroy {
    @ViewChild('toolbarContainer') toolbarContainerElement: ElementRef;

    @Input() attachments?: InboxAttachmentBase[];
    @Input() isFocusNeeded?: boolean;
    @Input() note: NoteGuiModel;
    @Input() noteId?: number = 0;
    @Input() quillRange: Range;
    @Input() toolbarSize?: ToolbarSize = 'medium';

    @Output() addNote: EventEmitter<void> = new EventEmitter();
    @Output() editorCreated: EventEmitter<any> = new EventEmitter();
    @Output() isInvalidChanged: EventEmitter<boolean> = new EventEmitter();
    @Output() updateQuillSelection: EventEmitter<void> = new EventEmitter();

    isInvalidMessage = true;
    quillEditor: Quill;
    quillConfig: unknown;

    private unsubscriber = new Subject<void>();

    constructor(
        public replyTemplateManagerService: ReplyTemplateManagerService,
        public noteService: NoteService,
        public quillHelperService: QuillHelperService,
        public toolbarHelperService: ToolbarHelperService,
        public replyTemplateService: ReplyTemplateService,
        private smilesService: SmilesService,
    ) {}

    ngOnInit() {
        this.quillConfig = this.quillHelperService.getNoteQuillConfig(this.addNoteOnCtrlEnter.bind(this));
    }

    hideToolbar() {
        this.toolbarContainerElement.nativeElement.classList.remove('active');
    }

    onEditorCreated(quill) {
        this.quillEditor = quill;

        if (this.isFocusNeeded) {
            this.quillEditor.setSelection(this.note.text.length);
            quill.focus();
        }

        this.editorCreated.emit(this.quillEditor);
    }

    onNoteTextChanged() {
        if (this.note.html) {
            this.note.text = HelperService.strip(
                this.smilesService.replaceImagesToCodes(this.noteService.getNoteWithProcessedMentions(this.note.html)),
                false,
            );
        } else {
            this.note.text = '';
        }

        this.isInvalidMessage = this.quillHelperService.checkQuillEmpty(this.quillEditor);

        this.isInvalidChanged.emit(this.isInvalidMessage);
    }

    toggleToolbar() {
        this.toolbarContainerElement.nativeElement.classList.toggle('active');
    }

    private addNoteOnCtrlEnter() {
        if (!(this.isInvalidMessage && !this.note.attachments.length)) {
            this.addNote.emit();
        }
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
